import type { BookingQuoteResponse, BookingQuoteRoomConfiguration } from '@/data/service/bookingquote'

/**
 * BookingQuote class
 */
class BookingQuote {
  private _roomConfigurations = new Map<number, BookingQuoteRoomConfiguration>()
  private _extras = new Map<number, Breakdown>()
  private _includedExtras = new Map<number, Breakdown>()

  /**
   * Clears the booking quote data
   */
  clear() {
    this._roomConfigurations.clear()
    this._extras.clear()
    this._includedExtras.clear()
  }

  /**
   * Gets the included extras breakdown
   * @param {Number?} accommodationId The accommodation identifier or null
   */
  getIncludedExtras(accommodationId: number): Breakdown | undefined {
    return this._includedExtras.get(accommodationId)
  }

  /**
   * Saves the quote response to the state
   * @param {BookingQuoteResponse} data
   */
  save(data: BookingQuoteResponse) {
    /* Clear everything? */
    this.clear()

    /* Parse response */
    for (const configuration of data.roomConfigurations) {
      /* Accommodation id */
      const accommodationId = configuration.identifier

      /* Save the room configuration to the map */
      this._roomConfigurations.set(accommodationId, configuration)

      /* Extract extras */
      const extras = new Breakdown(
        configuration.totalPrice.map(v => v.extraBreakdown)
      )
      this._extras.set(accommodationId, extras)

      /* Extract included extras */
      const includedExtras = new Breakdown(
        configuration.totalPrice.map(v => v.includedExtraBreakdown)
      )
      this._includedExtras.set(accommodationId, includedExtras)
    }
  }
}

type BreakdownType = Record<string, number>

export class Breakdown {
  private map: Map<string, number>

  /**
   * Constructs the breakdown object
   */
  constructor(breakdown: BreakdownType | BreakdownType[]) {
    /* Convert passed object to Map */
    if (!Array.isArray(breakdown)) {
      this.map = new Map(Object.entries(breakdown))
      return
    }

    /* Merge objects into one, sum identical extras if any */
    const newBreakdown = new Map<string, number>()
    for (const groupObject of breakdown) {
      for (const [id, price] of Object.entries(groupObject)) {
        newBreakdown.set(
          id,
          (newBreakdown.get(id) || 0) + price
        )
      }
    }

    /* Save the map */
    this.map = newBreakdown
  }

  /**
   * Checks whether an id is present in the breakdown
   * @param {Number|String} id The id to check
   * @returns {Boolean}
   */
  has(id: number | string): boolean {
    return this.map.has(String(id))
  }

  /**
   * Gets the price of an item
   * @param id Item id
   */
  get(id: number | string): number | undefined {
    return this.map.get(String(id))
  }
}

export default new BookingQuote()
